import { useEffect } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { DocBlocks } from '../components/DocBlocks'
import { getAdjacentPages, getPage } from '../content'
import { useManualLocale } from '../i18n/LocaleContext'
import { manualUi } from '../i18n/messages'
import { manualPagePath } from '../paths'

export function DocPageView() {
  const { pageId = '' } = useParams()
  const { locale } = useManualLocale()
  const ui = manualUi(locale)
  const location = useLocation()
  const page = getPage(pageId, locale)
  const adjacent = getAdjacentPages(pageId, locale)
  const hash = location.hash.replace(/^#/, '')

  useEffect(() => {
    if (!page) return
    document.title = `${page.title} — ${ui.chapterDocumentTitle}`
  }, [page, ui.chapterDocumentTitle])

  useEffect(() => {
    if (!page) return
    if (!hash) {
      window.scrollTo(0, 0)
      return
    }
    const el = document.getElementById(hash)
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [page, pageId, hash])

  if (!page) return <Navigate to={manualPagePath('introduction')} replace />

  return (
    <article className="doc-page">
      <header className="doc-header">
        <p className="eyebrow">{page.category}</p>
        <h1>{page.title}</h1>
        <p className="lead">{page.description}</p>
      </header>

      {page.sections.length > 1 ? (
        <nav className="on-this-page" aria-label={ui.onThisPage}>
          <h2>{ui.onThisPage}</h2>
          <ol>
            {page.sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <DocBlocks blocks={page.blocks} />

      <footer className="doc-pager">
        {adjacent.prev ? (
          <Link to={manualPagePath(adjacent.prev.id)} className="pager-link prev">
            <span className="pager-label">{ui.previous}</span>
            <span className="pager-title">{adjacent.prev.title}</span>
          </Link>
        ) : (
          <span />
        )}
        {adjacent.next ? (
          <Link to={manualPagePath(adjacent.next.id)} className="pager-link next">
            <span className="pager-label">{ui.next}</span>
            <span className="pager-title">{adjacent.next.title}</span>
          </Link>
        ) : null}
      </footer>
    </article>
  )
}
